import { useMsal } from "@azure/msal-react";
import { Button } from "primereact/button";
import { Image } from "primereact/image";
import { Tooltip } from "primereact/tooltip";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ICONS from "../../Constants/Icons";
import { useTheme } from "../../Theme/ThemeContext";
import { RecentArchieveConversation } from "../../Models/ConversationApiState";
import { ArchievedRequest } from "../../Models/Request/SASUrlRequest";
import ConversationChat from "../../Pages/ConversationChat";
import Conversations from "../../Pages/ConversationsList";
import NavigationIcon from "../../Pages/NavigationIcon";
import { setHeaderFileNameTitle } from "../../Redux/Header/HeaderActions";
import {
  hideConversationPane,
  setDefaultConversation,
} from "../../Redux/ChatConversation/ChatConversationActions";
import {
  markConversationArchieve,
  markConversationRecent,
  updateConversationTitle,
} from "../../Redux/Conversation/ConverasationAction";
import { toggleUseExternalDocumentsModeFalse } from "../../Redux/Dashboard/DashboardActions";
import { setHeaderQuestionTitle } from "../../Redux/Header/HeaderActions";
import { closeRightSidebar } from "../../Redux/RightSidebar/RightSidebarActions";
import { toggleSidebar } from "../../Redux/Sidebar/SidebarActions";
import { AppDispatch, RootState } from "../../Redux/Store";
import { generateGuid, updateDocumentTitle } from "../../Utils/Common";

const filterLabels = ["Recent", "Archived"];

const SideBar: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const Icon = ICONS();
  const dispatch = useDispatch<AppDispatch>();
  const { recentConversation, archieveConversation } = useSelector(
    (state: RootState) => state.Conversation,
  );
  const { isOpen } = useSelector((state: RootState) => state.RightSidebar);
  const { conversationDetails, showConversationPane } = useSelector(
    (state: RootState) => state.ChatConversation,
  );
  const { instance } = useMsal();
  const [activeSidebar, setActiveSidebar] = useState<string | null>(null);
  const [activeDarkMode, setActiveDarkMode] = useState(isDark);

  const { success } = useSelector((state: RootState) => state.IUnderstand);
  const { hasAccess: hasAdminAccess } = useSelector(
    (state: RootState) => state.AdminAccess,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  const handleSidebarToggle = (isExpanded: boolean) => {
    if (isExpanded) {
      setActiveSidebar("Chats in this conversation");
    } else {
      setActiveSidebar(null);
    }
  };

  const renderComponent = () => {
    switch (activeSidebar) {
      case "Chats in this conversation":
        return <ConversationChat chats={conversationDetails.chats} />;
      case "Recent Conversations":
        return (
          <Conversations
            type="recent"
            conversations={recentConversation}
            onMarkRecentAndArchieveClick={onMarkRecentAndArchieveClick}
            filterLabels={filterLabels}
            onEditTitle={onEditTitle}
          />
        );
      case "Archived Conversations":
        return (
          <Conversations
            type="archived"
            conversations={archieveConversation}
            onMarkRecentAndArchieveClick={onMarkRecentAndArchieveClick}
            filterLabels={filterLabels}
            onEditTitle={onEditTitle}
          />
        );
      default:
        return <></>;
    }
  };

  useEffect(() => {
    setActiveDarkMode(isDark);
  }, [isDark]);

  useEffect(() => {
    // With only Document List available for now, skip the Admin nav-list
    // secondary panel entirely (on entry and while on any /admin route) and
    // let AdminPanel's own content fill the space; there's nothing to choose
    // between yet. Restore setting activeSidebar("Admin") on isAdminRoute
    // once more than one Admin section exists.
    if (activeSidebar === "Admin") {
      setActiveSidebar(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminRoute]);

  useEffect(() => {
    if (isOpen && !isAdminRoute) {
      setActiveSidebar(null);
    }
  }, [isOpen, activeSidebar, isAdminRoute]);

  useEffect(() => {}, [recentConversation, archieveConversation]);

  useEffect(() => {
    if (!showConversationPane && !isAdminRoute) {
      setActiveSidebar(null);
    }
  }, [showConversationPane, isAdminRoute]);

  const onMarkRecentAndArchieveClick = (
    conversation: RecentArchieveConversation,
    type: string,
    token: string | null,
  ) => {
    const isArchived = type === "recent";
    const requestData: ArchievedRequest = {
      ConversationId: conversation.id,
      IsArchieved: isArchived,
    };
    const action = isArchived
      ? markConversationArchieve
      : markConversationRecent;
    dispatch(action(token, requestData));
  };

  const onSidebarCloseClick = () => {
    if (isAdminRoute) return;
    setActiveSidebar(null);
    dispatch(toggleSidebar());
  };

  const onSidebarMenuClick = (menu: string) => {
    if (isAdminRoute) return;
    if (activeSidebar === menu) {
      setActiveSidebar(null);
    } else {
      setActiveSidebar(menu);
    }
    dispatch(closeRightSidebar());
    dispatch(toggleSidebar());
  };

  const onDarkmodeClick = () => {
    toggleTheme();
  };

  const onHelpClick = () => {
    window.open(`${process.env.REACT_APP_THINK_HELP_URL}`, "_blank");
  };

  const onLogoutClick = () => {
    instance.logoutRedirect();
  };

  const onEditTitle = (
    conversation: RecentArchieveConversation,
    value: boolean,
    token: string | null,
  ) => {
    if (token) {
      dispatch(updateConversationTitle(token, conversation, value));
    }
  };

  const onNewChatClick = () => {
    dispatch(setHeaderQuestionTitle(""));
    dispatch(closeRightSidebar());
    dispatch(hideConversationPane());
    dispatch(setDefaultConversation());
    dispatch(toggleUseExternalDocumentsModeFalse());
    updateDocumentTitle("", success);
    setActiveSidebar(null);
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split('/');
    if (pathSegments.length > 3) {
      if(currentPath.includes("/view/index")){
        navigate(`/`);
      }
      else{
        const basePath = `/${pathSegments[1]}/${pathSegments[2]}`;
        const newGuid = generateGuid();
        navigate(`${basePath}/${newGuid}`);
      }
    } else {
      navigate(`/`); 
    }
  };

  const onSkillsClick = async () => {
    try {
      dispatch(setHeaderFileNameTitle("Skills"));
      navigate("/skills");
    } catch (error) {
      console.error("Failed to navigate to Skills:", error);
    }
  };

  const onAdminClick = () => {
    dispatch(setHeaderFileNameTitle("Admin Console"));
    if (!isAdminRoute) {
      navigate("/admin");
    }
  };

  return (
    <div className="d-flex position-relative sidebar-container">
      <div className="sidebar">
        {success && (
          <>
            <span className="sidebar-img-span">
              <Image
                src={Icon.NewChatImg}
                alt="New Chat"
                className="sidebar-icon img-height"
                data-pr-tooltip="Start a new conversation"
                data-pr-position="right"
                onClick={onNewChatClick}
              />
            </span>
            <Tooltip target=".sidebar-icon" />

            <hr className="custom-hr" />

            <span
              className={`sidebar-img-span ${
                activeSidebar === "Recent Conversations" ? "active-menu" : ""
              }`}
              onClick={() => onSidebarMenuClick("Recent Conversations")}
            >
              <Image
                src={Icon.RecentConversationImg}
                alt="Recent Conversations"
                className="sidebar-icon img-height"
                data-pr-tooltip="Recent conversations"
                data-pr-position="right"
              />
            </span>

            <span
              className={`sidebar-img-span ${
                activeSidebar === "Archived Conversations" ? "active-menu" : ""
              }`}
              onClick={() => onSidebarMenuClick("Archived Conversations")}
            >
              <Image
                src={Icon.ArchieveConversationImg}
                alt="Archived Conversations"
                className="sidebar-icon"
                data-pr-tooltip="Archived conversations"
                data-pr-position="right"
                style={{ height: "20px" }}
              />
            </span>
            <span
              onClick={(e) => {
                e.preventDefault();
                onSkillsClick();
              }}
              className="mt-3 p-button p-button-text sidebar-tooltip icon cursor-pointer"
              data-pr-tooltip="Skills"
              data-pr-position="right"
              aria-label="Skills"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "0.20rem",
              }}
            >
              <span
                className="pi pi-th-large"
                style={{
                  fontSize: "1.2rem",
                  color: activeDarkMode ? "#ffffff" : "#4B4949",
                }}
              ></span>
            </span>

            {hasAdminAccess && (
              <span
                onClick={(e) => {
                  e.preventDefault();
                  onAdminClick();
                }}
                className="mt-3 p-button p-button-text sidebar-tooltip icon cursor-pointer"
                data-pr-tooltip="Admin"
                data-pr-position="right"
                aria-label="Admin"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "0.20rem",
                }}
              >
                <span
                  className="pi pi-cog"
                  style={{
                    fontSize: "1.2rem",
                    color: activeDarkMode ? "#ffffff" : "#4B4949",
                  }}
                ></span>
              </span>
            )}
          </>
        )}
        <Tooltip target=".sidebar-icon" />
        <Tooltip target=".sidebar-tooltip" />

        <span className="bottom-icons">
          <span
            className="sidebar-img-span light-dark-icon"
            onClick={onDarkmodeClick}
          >
            <Image
              src={Icon.DarkModeImg}
              alt="Dark Mode"
              className="sidebar-icon img-height"
              data-pr-tooltip={activeDarkMode ? "Light mode" : "Dark mode"}
              data-pr-position="right"
            />
          </span>

          <span className="sidebar-img-span" onClick={onHelpClick}>
            <Image
              src={Icon.HelpImg}
              alt="Help"
              className="sidebar-icon img-height"
              data-pr-tooltip="Help"
              data-pr-position="right"
            />
          </span>

          <span className="sidebar-img-span" onClick={onLogoutClick}>
            <Image
              src={Icon.LogoutImg}
              alt="Logout"
              className="sidebar-icon img-height"
              data-pr-tooltip="Logout"
              data-pr-position="right"
            />
          </span>
        </span>
      </div>

      {activeSidebar && (
        <>
          {!isAdminRoute && (
            <div className="sidebar-overlay" onClick={onSidebarCloseClick} />
          )}
          <div
            className={`secondary-sidebar ${
              activeSidebar === "Chats in this conversation"
                ? "chats-sidebar-active"
                : ""
            }`}
          >
            <div className="sidebar-header">
              {!isAdminRoute && <h3 className="sidebar-title">{activeSidebar}</h3>}
              {!isAdminRoute && (
                <Button
                  icon={
                    <Image
                      src={
                        activeSidebar === "Chats in this conversation"
                          ? Icon.dismissIcon
                          : Icon.PanelCloseImg
                      }
                      alt="Close Icon"
                      height={"24"}
                    />
                  }
                  className="sidebar-close-button "
                  aria-label="Close Button"
                  tooltip={
                    activeSidebar === "Chats in this conversation"
                      ? "Close Navigation"
                      : "Close"
                  }
                  onClick={onSidebarCloseClick}
                  style={{ width: "70px" }}
                />
              )}
            </div>
            {renderComponent()}
          </div>
        </>
      )}

      <NavigationIcon
        showConversationPane={showConversationPane}
        isOpen={isOpen}
        success={success}
        activeSidebar={activeSidebar}
        navigationIcon={Icon.NavigationIcon}
        handleSidebarToggle={handleSidebarToggle}
      />
    </div>
  );
};

export default SideBar;
